import { useEffect, useRef, useState } from 'react';

// Width at which the sidebar stops being an overlay drawer and becomes a
// permanent column. Must stay in sync with the `lg:` Tailwind classes used by
// components/sidebar, components/navbar and the layouts.
// NOTE: this project overrides Tailwind's default screens in tailwind.config.js
// (`lg: 992px`, not 1024px), which also happens to match Chakra's default `lg`.
export const SIDEBAR_DESKTOP_BREAKPOINT = 992;

const isDesktopWidth = () =>
  typeof window === 'undefined' ||
  window.innerWidth >= SIDEBAR_DESKTOP_BREAKPOINT;

/**
 * Owns the sidebar's open/closed state for a layout.
 *
 * Only *crossing* the breakpoint changes the state. The previous implementation
 * re-evaluated `innerWidth` on every resize event, so the few pixels gained or
 * lost when a page's scrollbar appeared after navigation were enough to force
 * the sidebar shut on sub-breakpoint laptops — it looked like clicking a
 * sidebar link closed it. Not reacting to same-side resizes also means a user
 * who closes the sidebar by hand on desktop keeps it closed.
 */
export default function useSidebarOpen() {
  const [open, setOpen] = useState(isDesktopWidth);
  const wasDesktop = useRef(isDesktopWidth());

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = isDesktopWidth();
      if (isDesktop === wasDesktop.current) return;
      wasDesktop.current = isDesktop;
      setOpen(isDesktop);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return [open, setOpen];
}
